import { delay } from 'lodash'
import auth0 from '@/services/auth0'
import { sendExceptionToAnalytics } from '@/services/googleAnalytics/errorHandling'
import { HELPERS } from '@/helpers'
import {
  SET_LOADING,
  GLOBAL_ERROR,
  SET_AUTH,
  SET_TOKEN,
  SET_EMAIL_SENT_STATUS,
  SET_FORM_SUBMITTED_STATUS,
  SET_AUTH_EMAIL,
  SET_BUTTON_LOADING_STATUS,
  SET_IS_VALID_EMAIL,
  SET_FORM_CHANGED_STATUS,
  RESET_REGISTRATION_STATE,
  SET_AUTH_INTENT,
} from '@/store/mutation-types'
import { AUTH_CONFIG } from '@/services/auth0/auth0-variables'
import { buildAuth0CallbackUrl } from '@/helpers'
import router from '@/router'

export default {
  storeToken: (
    context: any,
    authResult: { expiresIn: any; accessToken: any }
  ) => {
    if (!authResult) throw new Error('authResult is required')

    const expiresAt = JSON.stringify(
      authResult.expiresIn * 1000 + new Date().getTime()
    )
    localStorage.setItem('loggedIn', 'true')
    localStorage.setItem('expires_at', expiresAt)
    context.commit(SET_AUTH, true)
    context.commit(SET_TOKEN, authResult.accessToken)
  },
  auth0PasswordlessStart: (context: any, payload: any) => {
    // Mock API: Mirage stubs Auth0 start; resolve without contacting Auth0.js.
    if (import.meta.env.MODE === 'mockapi') {
      return Promise.resolve({
        email: payload.email,
        email_verified: false,
      })
    }

    const redirectUri = buildAuth0CallbackUrl(context.state.callbackLandingPage)

    return new Promise((resolve, reject) => {
      auth0.passwordlessStart(
        {
          connection: 'email',
          send: payload.send,
          email: payload.email,
          authParams: {
            redirectUri,
          },
        },
        function (err: any, res: any) {
          if (err) {
            context.commit(GLOBAL_ERROR, err.description)
            sendExceptionToAnalytics(
              `/src/store/modules/auth.js#auth0PasswordlessStart: ${err.error}`
            )
            reject(err)
            return reject(err)
          } else {
            resolve(res)
          }
        }
      )
    })
  },
  auth0PasswordlessLogin: (context: any, payload: any) => {
    // Mock API: skip Auth0; honour signup intent via createUser on GET /me.
    // Soft-navigate so Mirage onboarding state is not wiped by a full reload.
    if (import.meta.env.MODE === 'mockapi') {
      return context.dispatch('storeMockToken').then(async () => {
        try {
          const user = await context.dispatch('fetchUser')
          // Prefer Vuex after SET_USER (cloned payload). Auth actions see module
          // state as `state` and the donor module on `rootState`.
          const landingPage =
            context.rootState?.donor?.userDetails?.landingPage ??
            user?.data?.landingPage
          if (landingPage === 0) {
            await router.push({ name: 'DONOR_FLOW_ManageAccountCreateDetails' })
          } else if (landingPage === 1) {
            await router.push({
              name: 'DONOR_FLOW_ManageAccountCreateDeclaration',
            })
          } else if (context.state.authIntent === 'signup') {
            // Signup must never dump straight to the dashboard — recover by
            // forcing details.
            await router.push({ name: 'DONOR_FLOW_ManageAccountCreateDetails' })
          } else {
            await router.push({ name: 'Dashboard' })
          }
        } catch {
          await router.push({ name: 'DONOR_FLOW_ManageAccountUserNotFound' })
        }
      })
    }

    const redirectUri = buildAuth0CallbackUrl(context.state.callbackLandingPage)

    return new Promise((resolve, reject) => {
      auth0.passwordlessLogin(
        {
          connection: 'email',
          email: payload.email,
          verificationCode: payload.verificationCode,
          redirectUri,
        },
        (err: any, res: any) => {
          if (err) {
            context.commit(GLOBAL_ERROR, err.description)
            sendExceptionToAnalytics(
              `/src/store/modules/auth.js#auth0PasswordlessLogin: ${err.error}`
            )
            reject(err)
          } else {
            resolve(res)
          }
        }
      )
    })
  },
  auth0CheckSession: () => {
    if (import.meta.env.VITE_MAINTENANCE) return

    // Silent session probe only — do not surface GLOBAL_ERROR on timeout.
    // Logged-out visits and disallowed Auth0 origins (e.g. LAN IP) often fail here;
    // flashing a banner on /login is misleading.
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('auth0_check_session_timeout'))
      }, 10000)

      auth0.checkSession(
        {
          audience: AUTH_CONFIG.audience,
          scope: AUTH_CONFIG.scope,
        },
        async (error: any, authResult: any) => {
          clearTimeout(timeout)

          if (error) {
            reject(error)
            return
          }

          resolve(authResult)
        }
      )
    })
  },
  /**
   * Read tokens from the /callback URL hash after passwordless redirect.
   * Required on localhost: Auth0 will not skip consent, so checkSession fails
   * even though access_token / id_token are already in the hash.
   */
  auth0ParseHash: () => {
    return new Promise((resolve, reject) => {
      auth0.parseHash({ hash: window.location.hash }, (error, authResult) => {
        if (error) {
          reject(error)
          return
        }

        if (!authResult?.accessToken) {
          reject(new Error('No access token in Auth0 callback hash'))
          return
        }

        // Clear sensitive tokens from the address bar once consumed.
        window.history.replaceState(
          null,
          document.title,
          `${window.location.pathname}${window.location.search}`
        )

        resolve(authResult)
      })
    })
  },
  storeMockToken: (context: any) => {
    localStorage.setItem('loggedIn', 'true')
    // Long-lived expiry so mock sessions are not interrupted by Auth0 renew attempts.
    localStorage.setItem(
      'expires_at',
      JSON.stringify(Date.now() + 24 * 60 * 60 * 1000)
    )
    context.commit(SET_AUTH, true)
    context.commit(SET_TOKEN, 'swiftaid-testtoken-1234567890')
  },
  auth0Logout: (context: any, logoutUrl = HELPERS.logoutUrl) => {
    // Mock API: never hit Auth0 — clear local session and soft-navigate to login.
    // Real Auth0 logout also fails for LAN IPs (e.g. 192.168.x.x) that are not in
    // the application's Allowed Logout URLs; use https://localhost:8080 for real auth.
    if (import.meta.env.MODE === 'mockapi') {
      localStorage.removeItem('loggedIn')
      localStorage.removeItem('expires_at')
      context.commit(SET_AUTH, null)
      context.commit(SET_TOKEN, null)
      context.commit(SET_LOADING, false)
      // Soft nav keeps Vuex alive — clear the email/code step so /login shows
      // the email screen again (mirrors a real Auth0 full-page return).
      context.commit(RESET_REGISTRATION_STATE)
      context.commit(SET_AUTH_INTENT, 'login')
      void router.push({ name: 'Login' })
      return
    }

    context.commit(SET_LOADING, true)
    auth0.logout({
      returnTo: logoutUrl,
      clientID: import.meta.env.VITE_AUTH_CLIENT_ID,
    })
    localStorage.removeItem('loggedIn')
    localStorage.removeItem('expires_at')
    context.commit(SET_AUTH, null)
    context.commit(SET_TOKEN, null)
  },
  sendLoginEmail: async (context: any, payload: any) => {
    // Prevent double Auth0 passwordless starts from repeated Continue / Resend taps.
    if (context.state.btnLoading) return

    context.commit(SET_AUTH_EMAIL, payload.email)
    context.commit(SET_BUTTON_LOADING_STATUS, true)

    if (import.meta.env.MODE === 'mockapi') {
      try {
        sessionStorage.setItem('mockSignupEmail', payload.email)
        // Re-arm the delete* scheduled-deletion mock each time they enter an email.
        sessionStorage.removeItem('mockDeletionCancelled')
      } catch {
        // ignore
      }
    }

    try {
      await context.dispatch('auth0PasswordlessStart', {
        email: payload.email,
        send: payload.send,
      })
      delay(() => {
        context.commit(SET_FORM_SUBMITTED_STATUS, true)
        context.commit(SET_BUTTON_LOADING_STATUS, false)
      }, 1000)
    } catch (error) {
      context.commit(SET_BUTTON_LOADING_STATUS, false)
    }
  },
  setEmailSentStatus: (context: any, payload: any) => {
    context.commit(SET_EMAIL_SENT_STATUS, payload)
  },
  setFormSubmittedStatus: (context: any, payload: any) => {
    context.commit(SET_FORM_SUBMITTED_STATUS, payload)
  },
  setAuthEmail: (context: any, payload: any) => {
    context.commit(SET_AUTH_EMAIL, payload)
  },
  setButtonLoadingStatus: (context: any, payload: any) => {
    context.commit(SET_BUTTON_LOADING_STATUS, payload)
  },
  setIsValidEmail: (context: any, payload: any) => {
    context.commit(SET_IS_VALID_EMAIL, payload)
  },
  setFormChangedStatus: (context: any, payload: any) => {
    context.commit(SET_FORM_CHANGED_STATUS, payload)
  },
  resetRegistrationState: (context: any) => {
    context.commit(RESET_REGISTRATION_STATE)
  },
  getToken: (context: any) =>
    new Promise((resolve) => {
      resolve(context.state.idToken)
    }),
}
